import { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, Shield, FileText, User, Building2, Calendar, Hash, Activity, Download } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WORK_TYPE_LABELS = {
  script: { label: 'Script', color: 'text-blue-700', bg: 'bg-blue-50', border: 'border-blue-200' },
  file: { label: 'File', color: 'text-emerald-700', bg: 'bg-emerald-50', border: 'border-emerald-200' },
  music: { label: 'Music', color: 'text-purple-700', bg: 'bg-purple-50', border: 'border-purple-200' },
  artwork: { label: 'Artwork', color: 'text-amber-700', bg: 'bg-amber-50', border: 'border-amber-200' },
  screenplay: { label: 'Screenplay', color: 'text-rose-700', bg: 'bg-rose-50', border: 'border-rose-200' },
  character: { label: 'Character', color: 'text-cyan-700', bg: 'bg-cyan-50', border: 'border-cyan-200' },
  composition: { label: 'Composition', color: 'text-indigo-700', bg: 'bg-indigo-50', border: 'border-indigo-200' },
  other: { label: 'Other', color: 'text-gray-700', bg: 'bg-gray-50', border: 'border-gray-200' },
};

// ── Mock Live Activity Feed ──

const MOCK_NAMES = [
  'Aarav Malhotra', 'Priya Nair', 'Kabir Sethi', 'Meera Joshi', 'Dev Kapoor',
  'Ananya Das', 'Rohan Gupta', 'Simran Kaur', 'Aditya Sharma', 'Zara Khan',
  'Vikram Iyer', 'Nisha Reddy', 'Arjun Patel', 'Diya Pandey', 'Rahul Mehta',
];

const MOCK_ACTIONS = [
  { action: 'registered', detail: 'a screenplay', type: 'screenplay', entity: 'SCR' },
  { action: 'registered', detail: 'a musical composition', type: 'composition', entity: 'FIL' },
  { action: 'registered', detail: 'a character design portfolio', type: 'character', entity: 'FIL' },
  { action: 'registered', detail: 'a film script', type: 'script', entity: 'SCR' },
  { action: 'registered', detail: 'original artwork collection', type: 'artwork', entity: 'FIL' },
  { action: 'looked up', detail: 'registry entry', type: null, entity: 'REG' },
  { action: 'verified ownership of', detail: 'a registered script', type: 'script', entity: 'SCR' },
  { action: 'transferred rights for', detail: 'a composition', type: 'composition', entity: 'FIL' },
  { action: 'registered', detail: 'a documentary treatment', type: 'script', entity: 'SCR' },
  { action: 'looked up', detail: 'IP ownership record', type: null, entity: 'REG' },
  { action: 'registered', detail: 'a song lyric sheet', type: 'music', entity: 'FIL' },
  { action: 'registered', detail: 'a VFX asset pack', type: 'file', entity: 'FIL' },
  { action: 'transferred rights for', detail: 'a short film', type: 'file', entity: 'FIL' },
];

const MOCK_SALE_ACTIONS = [
  { detail: 'a screenplay', type: 'script', entity: 'SCR' },
  { detail: 'music rights', type: 'music', entity: 'FIL' },
  { detail: 'a character IP', type: 'character', entity: 'FIL' },
  { detail: 'a film script', type: 'script', entity: 'SCR' },
];

const MOCK_ORGS = ['Dharma', 'YRF', 'Excel Ent.', 'TVF', 'Phantom', 'Sikhya'];

const randomId = (prefix) => `AINAA-${prefix}-${Math.random().toString(36).substring(2, 7).toUpperCase()}`;
const randomRegId = () => `AINAA-REG-${Math.random().toString(36).substring(2, 10).toUpperCase()}-${new Date().toISOString().slice(2,4)}${String(new Date().getMonth()+1).padStart(2,'0')}${String(new Date().getDate()).padStart(2,'0')}`;
const randomIP = () => `${Math.floor(Math.random()*200)+10}.${Math.floor(Math.random()*255)}.${Math.floor(Math.random()*255)}.${Math.floor(Math.random()*255)}`;

const generateMockEntry = () => {
  const name = MOCK_NAMES[Math.floor(Math.random() * MOCK_NAMES.length)];
  const isSale = Math.random() < 0.2;
  const now = new Date();
  const time = now.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  const date = now.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
  const ip = randomIP();

  if (isSale) {
    const sale = MOCK_SALE_ACTIONS[Math.floor(Math.random() * MOCK_SALE_ACTIONS.length)];
    const buyer = Math.random() > 0.5
      ? MOCK_ORGS[Math.floor(Math.random() * MOCK_ORGS.length)]
      : MOCK_NAMES[Math.floor(Math.random() * MOCK_NAMES.length)];
    const pplId = randomId('PPL');
    const buyerId = Math.random() > 0.5 ? randomId('ORG') : randomId('PPL');
    const entityId = sale.entity === 'SCR' ? randomId('SCR') : randomId('FIL');
    return {
      id: Math.random().toString(36).substring(2),
      timestamp: now.toISOString(),
      name, pplId,
      action: 'sold', detail: sale.detail,
      buyer, buyerId,
      entityId, type: sale.type, time, date, ip,
      isSale: true, priceHidden: true,
    };
  }

  const act = MOCK_ACTIONS[Math.floor(Math.random() * MOCK_ACTIONS.length)];
  const isOrg = Math.random() < 0.15;
  const ownerName = isOrg ? MOCK_ORGS[Math.floor(Math.random() * MOCK_ORGS.length)] : name;
  const pplId = isOrg ? randomId('ORG') : randomId('PPL');
  const entityId = act.entity === 'REG' ? randomRegId() : randomId(act.entity);

  return {
    id: Math.random().toString(36).substring(2),
    timestamp: now.toISOString(),
    name: ownerName, pplId, action: act.action, detail: act.detail,
    entityId, type: act.type, time, date, ip,
  };
};

const LiveFeedEntry = ({ entry, index }) => {
  const wt = entry.type ? (WORK_TYPE_LABELS[entry.type] || WORK_TYPE_LABELS.other) : null;
  return (
    <motion.div
      initial={{ opacity: 0, y: -20, height: 0 }}
      animate={{ opacity: 1, y: 0, height: 'auto' }}
      transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
      className={`px-5 py-3 border-b border-[#F0EDE8] ${index === 0 ? 'bg-[#F8F7F5]' : 'bg-white'} transition-colors duration-500`}
    >
      <div className="flex items-start gap-3">
        <span className="text-[10px] text-[#D8D3CB] font-medium flex-shrink-0 w-[60px] pt-0.5">{entry.time}</span>
        <div className="flex-1 min-w-0">
          <p className="text-sm text-[#0F1B2D] leading-relaxed">
            <span className="font-semibold text-[#C4A35A] font-mono text-xs">{entry.pplId}</span>
            {entry.isSale ? (
              <>
                <span className="text-[#4A4640]"> sold </span>
                <span className="font-medium text-[#0F1B2D]">{entry.detail}</span>
                <span className="text-[#4A4640]"> to </span>
                <span className="font-semibold text-blue-600 font-mono text-xs">{entry.buyerId}</span>
                {entry.priceHidden && (
                  <span className="text-[9px] text-[#D8D3CB] ml-2 italic">[amount visible to parties only]</span>
                )}
              </>
            ) : (
              <>
                <span className="text-[#4A4640]"> {entry.action} </span>
                <span className="font-medium text-[#0F1B2D]">{entry.detail}</span>
                {' '}
                <span className="font-mono text-xs text-blue-600">{entry.entityId}</span>
              </>
            )}
          </p>
          <div className="flex items-center gap-3 mt-1">
            <span className="text-[10px] text-[#9B9589]">{entry.date}</span>
            <span className="text-[10px] text-[#D8D3CB]">IP {entry.ip}</span>
            {wt && <span className={`text-[8px] font-bold uppercase tracking-wider px-1.5 py-0.5 ${wt.bg} ${wt.color} border ${wt.border}`}>{wt.label}</span>}
            {entry.isSale && <span className="text-[8px] font-bold uppercase tracking-wider px-1.5 py-0.5 bg-amber-50 text-amber-700 border border-amber-200">Sale</span>}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

const LiveActivityFeed = () => {
  const [entries, setEntries] = useState(() => Array.from({ length: 8 }, generateMockEntry));
  const intervalRef = useRef(null);

  useEffect(() => {
    intervalRef.current = setInterval(() => {
      setEntries(prev => {
        const newEntry = generateMockEntry();
        return [newEntry, ...prev.slice(0, 19)];
      });
    }, 3000 + Math.random() * 2000);
    return () => clearInterval(intervalRef.current);
  }, []);

  return (
    <div className="border-2 border-[#D8D3CB] bg-white overflow-hidden" data-testid="live-activity-feed">
      <div className="bg-[#F8F7F5] border-b-2 border-[#D8D3CB] px-5 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
          <span className="text-[10px] font-bold text-[#0F1B2D] uppercase tracking-wider">Live Registry Activity</span>
        </div>
        <span className="text-[10px] text-[#9B9589]">Auto-updating</span>
      </div>
      <div className="max-h-[400px] overflow-y-auto" style={{ scrollbarWidth: 'thin' }}>
        <AnimatePresence initial={false}>
          {entries.map((entry, i) => (
            <LiveFeedEntry key={entry.id} entry={entry} index={i} />
          ))}
        </AnimatePresence>
      </div>
    </div>
  );
};


// ── Main Registry Page ──

const ScriptRegistry = () => {
  const [query, setQuery] = useState('');
  const [result, setResult] = useState(null);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!query.trim()) return;
    setLoading(true);
    setResult(null);
    setResults(null);
    setSearched(true);
    try {
      if (query.toUpperCase().startsWith('AINAA-REG-') && query.length > 12) {
        const { data } = await axios.get(`${API}/registry/lookup/${query.toUpperCase()}`);
        setResult(data);
      } else {
        const { data } = await axios.get(`${API}/registry/lookup?q=${encodeURIComponent(query)}`);
        setResults(data);
      }
    } catch (err) {
      if (err.response?.status === 404) toast.error('No registry entry found for this ID');
      else toast.error('Search failed');
    } finally { setLoading(false); }
  };

  const renderEntry = (entry) => {
    const wt = WORK_TYPE_LABELS[entry.work_type] || WORK_TYPE_LABELS.other;
    return (
      <motion.div key={entry.registry_id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
        className="border-2 border-[#0F1B2D] bg-white p-6" data-testid={`registry-entry-${entry.registry_id}`}>
        <div className="flex items-start justify-between mb-5">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className={`text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 border ${wt.bg} ${wt.color} ${wt.border}`}>{wt.label}</span>
              {entry.status === 'registered' && (
                <span className="text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 bg-emerald-50 text-emerald-700 border border-emerald-200">Verified</span>
              )}
            </div>
            <h3 className="font-['Playfair_Display'] text-xl font-semibold text-[#0F1B2D]">{entry.title}</h3>
            {entry.description && <p className="text-sm text-[#4A4640] mt-1">{entry.description}</p>}
          </div>
          <p className="font-mono text-sm font-bold text-[#C4A35A] flex-shrink-0" data-testid="registry-id">{entry.registry_id}</p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5">
          <div className="flex items-center gap-2">
            {entry.owner_type === 'ORG' ? <Building2 size={14} className="text-[#9B9589]" /> : <User size={14} className="text-[#9B9589]" />}
            <div>
              <p className="text-[9px] text-[#9B9589] font-bold uppercase">Owner</p>
              <p className="text-xs font-semibold text-[#0F1B2D]">{entry.owner_name}</p>
              <p className="text-[10px] font-mono text-[#C4A35A]">{entry.owner_ainaa_id}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Calendar size={14} className="text-[#9B9589]" />
            <div>
              <p className="text-[9px] text-[#9B9589] font-bold uppercase">Registered</p>
              <p className="text-xs font-semibold text-[#0F1B2D]">{new Date(entry.registered_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' })}</p>
            </div>
          </div>
          {entry.file && (
            <div className="flex items-center gap-2">
              <FileText size={14} className="text-[#9B9589]" />
              <div>
                <p className="text-[9px] text-[#9B9589] font-bold uppercase">File</p>
                <p className="text-xs font-semibold text-[#0F1B2D]">{entry.file.filename}</p>
                <p className="text-[10px] text-[#9B9589]">{(entry.file.size / 1024).toFixed(0)} KB</p>
              </div>
            </div>
          )}
        </div>

        <div className="bg-[#EDECEA] border border-[#D8D3CB] p-3">
          <div className="flex items-center gap-2">
            <Hash size={12} className="text-[#9B9589]" />
            <span className="text-[9px] text-[#9B9589] font-bold uppercase">Verification Hash (SHA-256)</span>
          </div>
          <code className="text-[10px] font-mono text-[#4A4640] break-all block mt-1">{entry.verification_hash}</code>
        </div>

        <div className="mt-4 flex justify-end">
          <button
            onClick={async () => {
              try {
                const res = await axios.get(`${API}/registry/certificate/${entry.registry_id}`, { responseType: 'blob' });
                const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                const a = document.createElement('a');
                a.href = url;
                a.download = `AINAA-Certificate-${entry.registry_id}.pdf`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                window.URL.revokeObjectURL(url);
                toast.success('Certificate downloaded');
              } catch {
                toast.error('Failed to generate certificate');
              }
            }}
            className="btn-ainaa-primary px-5 py-2.5 text-xs font-bold uppercase tracking-wider inline-flex items-center gap-2"
            data-testid="download-certificate-btn"
          >
            <Download size={14} />
            Download Certificate
          </button>
        </div>
      </motion.div>
    );
  };

  return (
    <div className="min-h-screen bg-[#EDECEA] flex flex-col">
      <Navigation />
      <div className="flex-1 pt-24 pb-16 px-6 md:px-12">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="text-center mb-10">
            <p className="text-[10px] uppercase tracking-[0.3em] text-[#C4A35A] font-bold mb-3">Public Index</p>
            <h1 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-semibold text-[#0F1B2D] mb-3">
              AINAA Registry
            </h1>
            <p className="text-sm text-[#4A4640] font-medium max-w-lg mx-auto">
              Look up registered creative works — scripts, compositions, character designs, and more. 
              Every entry is timestamped with a unique ID and SHA-256 verification hash.
            </p>
          </div>

          {/* Search */}
          <form onSubmit={handleSearch} className="flex gap-3 mb-8">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-[#9B9589]" size={16} />
              <Input type="text" value={query} onChange={(e) => setQuery(e.target.value)}
                placeholder="Enter Registry ID (AINAA-REG-XXXXXXXX) or search by title..."
                className="input-ainaa pl-11 h-12" data-testid="registry-search-input" />
            </div>
            <button type="submit" disabled={loading}
              className="btn-ainaa-primary px-6 py-3 text-sm font-semibold uppercase tracking-wider flex-shrink-0"
              data-testid="registry-search-btn">
              {loading ? 'Searching...' : 'Lookup'}
            </button>
          </form>

          {result && renderEntry(result)}

          {results && (
            <div className="space-y-4">
              <p className="text-xs text-[#9B9589] font-medium">{results.total} result{results.total !== 1 ? 's' : ''} found</p>
              {results.items?.length > 0 ? results.items.map(renderEntry) : (
                <div className="border border-[#D8D3CB] bg-white p-12 text-center">
                  <Shield className="w-10 h-10 text-[#D8D3CB] mx-auto mb-3" />
                  <p className="text-sm text-[#9B9589] font-medium">No registered works found</p>
                </div>
              )}
            </div>
          )}

          {searched && !result && !results && !loading && (
            <div className="border border-[#D8D3CB] bg-white p-12 text-center">
              <Shield className="w-10 h-10 text-[#D8D3CB] mx-auto mb-3" />
              <p className="text-sm text-[#9B9589] font-medium">No registry entry found</p>
            </div>
          )}

          {/* Live Activity Feed + Info */}
          {!searched && (
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 mt-2">
              {/* Live Feed — takes 3/5 */}
              <div className="lg:col-span-3">
                <LiveActivityFeed />
              </div>

              {/* Info cards — takes 2/5 */}
              <div className="lg:col-span-2 space-y-4">
                <div className="border border-[#D8D3CB] bg-white p-5">
                  <FileText size={18} className="text-[#C4A35A] mb-3" />
                  <h4 className="text-sm font-bold text-[#0F1B2D] mb-1">Register Works</h4>
                  <p className="text-xs text-[#9B9589] leading-relaxed">
                    Register scripts, compositions, artworks, and more via Slate, Lobby, or the AINAA mobile app. 
                    Each work receives a unique AINAA-REG ID and SHA-256 hash.
                  </p>
                </div>
                <div className="border border-[#D8D3CB] bg-white p-5">
                  <Shield size={18} className="text-[#C4A35A] mb-3" />
                  <h4 className="text-sm font-bold text-[#0F1B2D] mb-1">Prove Ownership</h4>
                  <p className="text-xs text-[#9B9589] leading-relaxed">
                    Timestamped entries with cryptographic hashes provide verifiable proof of creation. 
                    Blockchain upgrade coming soon.
                  </p>
                </div>
                <div className="border border-[#D8D3CB] bg-white p-5">
                  <Activity size={18} className="text-[#C4A35A] mb-3" />
                  <h4 className="text-sm font-bold text-[#0F1B2D] mb-1">Track Everything</h4>
                  <p className="text-xs text-[#9B9589] leading-relaxed">
                    Every registration, lookup, and transfer is logged publicly. 
                    Full transparency for IP ownership across the creative industry.
                  </p>
                </div>

                <div className="bg-[#0F1B2D] p-5 text-center">
                  <p className="text-[10px] uppercase tracking-[0.2em] text-[#C4A35A] font-bold mb-2">How to Register</p>
                  <p className="text-xs text-[#B0ACA4] leading-relaxed">
                    Files are registered via connected platforms — Slate, Lobby, or the AINAA mobile app.
                    Use your AINAA API key to register works programmatically.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default ScriptRegistry;
